import React, { ComponentType, LazyExoticComponent } from 'react';

/**
 * Chunk-load resilience for `React.lazy` (P-COND-0FC032BB33AF).
 *
 * A redeploy replaces the hashed chunks, so a tab opened before it can 404 on
 * its next dynamic import. Recovery order: retry the import (transient network
 * blips), then one guarded auto-reload (stale hash), and only then let the
 * error reach App.tsx::ChunkErrorBoundary (manual reload page).
 */

/** Backoff before each re-attempt of a failed chunk import. */
export const CHUNK_RETRY_DELAYS_MS: readonly number[] = [1000, 3000];
/** At most one automatic page reload per this window (avoids reload loops). */
export const CHUNK_RELOAD_GUARD_MS = 60_000;
/** sessionStorage key holding the timestamp of the last automatic reload. */
export const CHUNK_RELOAD_STORAGE_KEY = 'conductor_chunk_reload_at';

type ModuleFactory<T> = () => Promise<T>;

/** True for webpack JS (`ChunkLoadError`) and mini-css-extract CSS chunk failures. */
export const isChunkLoadError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const e = err as { name?: unknown; code?: unknown; message?: unknown };
  if (e.name === 'ChunkLoadError' || e.code === 'CSS_CHUNK_LOAD_FAILED') return true;
  return typeof e.message === 'string' && /Loading (CSS )?chunk [^ ]+ failed/i.test(e.message);
};

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Call `factory`; on a chunk-load error retry after each delay in
 * CHUNK_RETRY_DELAYS_MS (webpack drops the failed chunk record, so a re-import
 * re-requests it). Non-chunk errors are rethrown immediately.
 */
export const retryImport = async <T>(factory: ModuleFactory<T>): Promise<T> => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await factory();
    } catch (err) {
      if (!isChunkLoadError(err) || attempt >= CHUNK_RETRY_DELAYS_MS.length) throw err;
      await sleep(CHUNK_RETRY_DELAYS_MS[attempt]);
    }
  }
};

/** Record and permit an auto-reload unless one happened within the guard window. */
const claimAutoReload = (): boolean => {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) || 0);
    if (Date.now() - last < CHUNK_RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(Date.now()));
    return true;
  } catch {
    return false; // storage unavailable — cannot guard a loop, fall back to manual page
  }
};

/**
 * retryImport + one guarded auto-reload: after retries are exhausted on a
 * chunk error, reloads the page and never settles (Suspense keeps showing its
 * fallback until the reload lands); otherwise rethrows to the error boundary.
 */
export const loadWithAutoReload = <T>(factory: ModuleFactory<T>): Promise<T> =>
  retryImport(factory).catch((err) => {
    if (isChunkLoadError(err) && claimAutoReload()) {
      window.location.reload();
      return new Promise<T>(() => {});
    }
    throw err;
  });

/** Drop-in replacement for `lazy(() => import('./X'))`. */
export const lazyWithRetry = <T extends ComponentType<any>>(
  factory: ModuleFactory<{ default: T }>,
): LazyExoticComponent<T> => React.lazy(() => loadWithAutoReload(factory));
